import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, setToken, formatApiError } from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [checking, setChecking] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", password: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .get(`/team/invitations/verify?token=${encodeURIComponent(token)}`)
      .then((r) => setInvitation(r.data))
      .catch(() => setInvitation({ valid: false }))
      .finally(() => setChecking(false));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Mot de passe : 8 caractères minimum.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/team/accept-invitation", {
        token,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        password: form.password,
      });
      setToken(data.access_token);
      toast.success("Bienvenue dans l'équipe !");
      await refresh();
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <AuthShell title="Vérification…">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </AuthShell>
    );
  }

  if (!invitation?.valid) {
    return (
      <AuthShell
        title="Invitation invalide"
        subtitle="Ce lien est expiré, révoqué ou incorrect."
        footer={
          <Link to="/login" className="text-[#C4553B] hover:underline">
            Aller à la connexion
          </Link>
        }
      >
        <p className="text-sm text-slate-400">
          Demandez à l'administrateur de votre entreprise de renvoyer une invitation.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={`Rejoindre ${invitation.company_name}`}
      subtitle={`Complétez votre inscription en tant que ${invitation.role}.`}
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="accept-invite-form">
        <div className="rounded border border-slate-700 bg-slate-950/40 p-3 text-sm text-slate-300">
          <div className="text-xs uppercase tracking-wide text-slate-500">Invité par email</div>
          <div className="mt-1 font-mono" data-testid="invite-email-display">
            {invitation.email}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-slate-300">Prénom</Label>
            <Input
              required
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className="border-slate-700 bg-slate-950/40"
              data-testid="accept-firstname"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">Nom</Label>
            <Input
              required
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className="border-slate-700 bg-slate-950/40"
              data-testid="accept-lastname"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-300">Mot de passe (8 caractères min.)</Label>
          <PasswordInput
            required
            minLength={8}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="border-slate-700 bg-slate-950/40"
            testIdPrefix="accept-password"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-300">Confirmer</Label>
          <PasswordInput
            required
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            className="border-slate-700 bg-slate-950/40"
            testIdPrefix="accept-confirm"
          />
        </div>
        {error && (
          <div className="rounded border border-red-500/30 bg-red-500/10 p-2.5 text-sm text-red-300">
            {error}
          </div>
        )}
        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-[#C4553B] text-white hover:bg-[#A8402C]"
          data-testid="accept-submit"
        >
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Rejoindre l'équipe
        </Button>
      </form>
    </AuthShell>
  );
}
